import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { PrimaryButton, SecondaryButton } from '@/components/index';

interface SaveQueryDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (queryName: string, isDefault?: boolean) => void;
}

interface FormData {
  queryName: string;
  isDefault: boolean;
}

export const SaveQueryDialog: React.FC<SaveQueryDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      queryName: '',
      isDefault: false,
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFormSubmit = (data: FormData) => {
    if (data.queryName.trim()) {
      if (data.isDefault) {
        onSubmit(data.queryName.trim(), true);
      } else {
        onSubmit(data.queryName.trim());
      }
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save Query</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onFormSubmit)} sx={{ mt: 2 }}>
          <Controller
            name="queryName"
            control={control}
            rules={{
              required: 'Query name is required',
              minLength: {
                value: 3,
                message: 'Query name must be at least 3 characters',
              },
              maxLength: {
                value: 50,
                message: 'Query name must be at most 50 characters',
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Query Name"
                placeholder="Enter a name for this query"
                error={!!errors.queryName}
                helperText={errors.queryName?.message}
                autoFocus
              />
            )}
          />
          <Controller
            name="isDefault"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={(
                  <Checkbox
                    checked={field.value}
                    onChange={(event) => field.onChange(event.target.checked)}
                  />
                )}
                label="Use as default for this Logistics Object type"
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
        <PrimaryButton onClick={handleSubmit(onFormSubmit)} variant="contained">
          Save
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};
